import PropTypes from 'prop-types';
import { Text } from '@etg/wings';
import { ProductIcon, productIds } from '@eti/products';
import { css, etiColors } from '@eti/styles';
import { CartItem } from '@eti/components';
import { price as priceUtil } from '@eti/utils';
import { useTranslation } from '@eti/providers';

const markupStyles = css`
  margin-top: 0.5rem;
`;

const passStyles = css`
  padding-inline-end: 0.25rem;
`;

const availabilityStyles = css`
  color: ${etiColors.grayScale[10]};
  display: flex;
  font-size: 0.75rem;
  justify-content: space-between;
`;

const contentStyles = css`
  color: ${etiColors.grayScale[10]};
  display: flex;
  font-size: 0.75rem;
  justify-content: space-between;
  padding-left: 1.5rem;

  [dir='rtl'] & {
    padding-left: 0;
    padding-right: 1.5rem;
  }
`;

const travelerNamesStyles = css`
  color: ${etiColors.grayScale[10]};
  display: flex;
  font-size: 0.75rem;
  padding-left: 2rem;

  [dir='rtl'] & {
    padding-left: 0;
    padding-right: 2rem;
  }
`;

const cartTextStyles = css`
  color: ${etiColors.black};
  display: flex;
  font-size: 0.75rem;
  margin-top: 0.5rem;
  padding-left: 1.5rem;
  white-space: pre-wrap;

  [dir='rtl'] & {
    padding-left: 0;
    padding-right: 1.5rem;
  }
`;

const setKey = (index) => `key-${index}`;

const setTitle = (title, numberOfUnits) => (numberOfUnits ? `${numberOfUnits} ${title}` : title);

const hasToggle = (markup, text, travelers, shouldShowTravelers, availability) => {
  return (
    Boolean(availability) ||
    Boolean(typeof markup === 'number') ||
    Boolean(typeof text === 'string') ||
    (shouldShowTravelers && Array.isArray(travelers) && travelers.length > 0)
  );
};

const iconStyles = css`
  height: 1rem;
  width: 1rem;
`;

const ReceiptProducts = ({
  component: Component = CartItem,
  products = [],
  markupLabel,
  providerCostLabel,
  shouldShowTravelers = false,
  viewMode = '',
}) => {
  const { t } = useTranslation();
  return (
    <ul data-testid={`cart-content-products${viewMode}`}>
      {products.map(
        (
          {
            id,
            productSummaryAlternativeName,
            markup,
            numberOfUnits,
            title,
            price,
            text,
            travelers,
            availability,
          },
          index,
        ) => (
          <Component
            component="li"
            dataTestId={`cart-product-${productIds[id]}${viewMode}`}
            hasToggle={hasToggle(markup, text, travelers, shouldShowTravelers, availability)}
            icon={productIds[id] && <ProductIcon id={productIds[id]} type="neutral" />}
            iconStyles={iconStyles}
            key={setKey(index)}
            price={priceUtil.formatPrice(price)}
            priceDataTestId={`cart-product-${productIds[id]}-price${viewMode}`}
            title={setTitle(productSummaryAlternativeName || title, numberOfUnits)}
          >
            {Boolean(availability) && (
              <>
                {availability.airportList.map((departureAirport) => (
                  <div className={availabilityStyles} key={departureAirport}>
                    <Text className={contentStyles}>{departureAirport}</Text>
                    <div className={contentStyles}>
                      <Text className={passStyles}>{availability.numberOfPass}</Text>
                      <Text>{availability.passLabel}</Text>
                    </div>
                  </div>
                ))}
              </>
            )}
            {typeof markup === 'number' && (
              <div className={markupStyles}>
                <div className={contentStyles}>
                  <span>{markupLabel}</span>
                  <span>{priceUtil.formatPrice(markup)}</span>
                </div>
                <div className={contentStyles}>
                  <span>{providerCostLabel}</span>
                  <span>{priceUtil.formatPrice(price - markup)}</span>
                </div>
              </div>
            )}
            {shouldShowTravelers && Array.isArray(travelers) && travelers.length > 0 && (
              <>
                <div className={cartTextStyles}>{t('Receipt.Summary.Travellers')}</div>
                {travelers.map(({ firstName, lastName }) => (
                  <div className={travelerNamesStyles} key={`${firstName}-${lastName}`}>
                    {firstName} {lastName}
                  </div>
                ))}
              </>
            )}
            {typeof text === 'string' && <div className={cartTextStyles}>{text}</div>}
          </Component>
        ),
      )}
    </ul>
  );
};

ReceiptProducts.propTypes = {
  component: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  markupLabel: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      availability: PropTypes.any,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      numberOfUnits: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  providerCostLabel: PropTypes.string,
  shouldShowTravelers: PropTypes.bool,
  viewMode: PropTypes.string,
};

export default ReceiptProducts;
